"use client";

import { c as _c } from "react/compiler-runtime";
import { useUser } from "@/contexts/user-provider";
import { useWebSocketCallbackSubscription } from "@/hooks/use-websocket-subscription";
import { PERMISSIONS, hasPermission } from "@/lib/permissions";
import type { AlertDetail } from "@/types/alert";
import type { CommunicationConversationSummary, CommunicationMessageSummary } from "@/types/communication";
import { AlertType } from "@/types/enums/alert-type";
import { adjustSocialUnreadCount, getSocialAccountById, getSocialConversationIsRead, prependSocialConversation, updateSocialConversation, updateSocialMessage, upsertSocialMessage } from "@/query/comms/social";
import { markConversationNotificationsReadInCache } from "@/query/notifications/notifications";
const SOCIAL_ALERT_TYPES = [AlertType.COMMUNICATION_MESSAGE_RECEIVED,
// Echoes of messages the business account sent from anywhere (Meta's own
// apps included) arrive as COMMUNICATION_MESSAGE_SENT and carry the same
// payload shape, so they run through the same branch below.
AlertType.COMMUNICATION_MESSAGE_SENT, AlertType.COMMUNICATION_CONVERSATION_UPDATED];

/**
 * Single global subscription to social messaging websocket alerts, mirroring
 * use-chat/-fax/-email-realtime-cache. It writes every update into the React
 * Query cache — the sidebar unread badge, the conversation list, and the open
 * thread's messages — so the inbox page (and the sidebar badge) react without
 * opening their own subscription.
 */
export function useSocialRealtimeCache() {
  const $ = _c(3);
  const {
    currentUser
  } = useUser();
  let t0;
  if ($[0] !== currentUser) {
    t0 = hasPermission(currentUser, PERMISSIONS.MESSAGING_SOCIAL_READ);
    $[0] = currentUser;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const canReadSocial = t0;
  let t1;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = function handleMessage(message) {
      const {
        type,
        data
      } = message as {
        type: string;
        data: Record<string, any>;
      };
      if (type === AlertType.COMMUNICATION_CONVERSATION_UPDATED) {
        const convId = data.conversationId as string;
        updateSocialConversation(convId, c => ({
          ...c,
          ...(data.deliveredWatermark && {
            deliveredWatermark: data.deliveredWatermark
          }),
          ...(data.seenWatermark && {
            seenWatermark: data.seenWatermark
          }),
          ...(data.lastMessagePreview !== undefined && {
            lastMessagePreview: data.lastMessagePreview
          })
        }));
        if (data.messageId) {
          updateSocialMessage(convId, data.messageId as string, m => ({
            ...m,
            ...(data.reactions !== undefined && {
              reactions: data.reactions
            }),
            ...(data.contentType && {
              contentType: data.contentType
            })
          }));
        }
        if (data.isRead) {
          adjustSocialUnreadCount(-1);
          markConversationNotificationsReadInCache(convId);
          updateSocialConversation(convId, _temp);
        }
        return;
      }
      if (type === AlertType.COMMUNICATION_MESSAGE_RECEIVED || type === AlertType.COMMUNICATION_MESSAGE_SENT) {
        const convId_0 = data.conversationId as string;
        const isOutbound = data.direction === "OUTBOUND";
        const isHumanOutbound = isOutbound && !data.aiGenerated;
        const wasUnreadHere = getSocialConversationIsRead(convId_0) === false;
        const newMsg = {
          id: data.messageId,
          conversationId: convId_0,
          direction: data.direction,
          contentType: data.contentType,
          body: data.body,
          createdDate: data.createdDate,
          mediaUrls: data.mediaUrls ?? null,
          replyToMessageId: data.replyToMessageId ?? null,
          messageMetadata: data.messageMetadata ?? null,
          reactions: null,
          senderProviderId: null,
          errorMessage: null
        };
        upsertSocialMessage(convId_0, newMsg);
        if (data.isNewConversation) {
          const newConv = {
            id: convId_0,
            channel: data.channel,
            externalThreadId: data.externalThreadId,
            name: data.name,
            username: data.username,
            profilePicUrl: data.profilePicUrl as string ?? null,
            lastMessageAt: data.createdDate,
            lastMessagePreview: data.lastMessagePreview,
            isRead: isHumanOutbound || data.conversationIsRead === true,
            status: "OPEN",
            patientId: null,
            lastInboundAt: isOutbound ? null : data.createdDate,
            deliveredWatermark: null,
            seenWatermark: null,
            createdDate: data.createdDate,
            account: getSocialAccountById(data.accountId as string | undefined)
          };
          prependSocialConversation(newConv);
        } else {
          updateSocialConversation(convId_0, c_1 => ({
            ...c_1,
            lastMessageAt: data.createdDate,
            lastMessagePreview: data.lastMessagePreview,
            isRead: isHumanOutbound || data.conversationIsRead === true,
            ...(!isOutbound && {
              lastInboundAt: data.createdDate
            })
          }));
        }
        if (type === AlertType.COMMUNICATION_MESSAGE_RECEIVED && !isOutbound && data.conversationBecameUnread && !wasUnreadHere) {
          adjustSocialUnreadCount(1);
        }
        if (data.conversationBecameRead) {
          adjustSocialUnreadCount(-1);
          markConversationNotificationsReadInCache(convId_0);
        }
      }
    };
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const handleMessage = t1;
  useWebSocketCallbackSubscription(SOCIAL_ALERT_TYPES, handleMessage, canReadSocial, "useSocialRealtimeCache");
}
function _temp(c_0) {
  return {
    ...c_0,
    isRead: true
  };
}